import { useCallback, useEffect, useState } from "react";
import { CalendarIcon, Filter, Loader2, RefreshCw, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getDistributionProcessesAll } from "@/Api/distributions";
import { searchContestsData } from "@/Api/contests";
import { getSellersDataWithFilters } from "@/Api/sellers";
import { getProductGroupsData } from "@/Api/productGroups";
import { getSelectUnitiesOnlyActives } from "@/Api/unities";
import { toast } from "sonner";
import { TableData } from "@/App/Distributions/SellerToUnityHistory/Table";
import { SearchableSelect } from "@/App/Distributions/History/components/SearchableSelect";
import PageTemplate from "@/components/PageTemplate";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";

interface FilterData {
  contest_status_id?: number;
  contest_id?: number;
  lottery_id?: number;
  seller_id?: number;
  start_date?: Date;
  end_date?: Date;
  status_id?: number;
  from_seller_id?: number;
}

const processStatusOptions = [
  { value: "1", label: "Pendente", color: "bg-yellow-500" },
  { value: "2", label: "Aceito", color: "bg-green-500" },
  { value: "3", label: "Rejeitado", color: "bg-red-500" },
];

const contestStatusOptions = [
  { value: "0", label: "Ativo" },
  { value: "1", label: "Encerrado" },
];

export default function SellerToUnityHistoryPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterData>({});
  const [showFilters, setShowFilters] = useState(false);

  const [selectedContest, setSelectedContest] = useState<any>(null);
  const [selectedSeller, setSelectedSeller] = useState<any>(null);
  const [selectedLottery, setSelectedLottery] = useState<any>(null);
  const [selectedSenderSeller, setSelectedSenderSeller] = useState<any>(null);
  const [selectedUnity, setSelectedUnity] = useState<any>(null);

  const [lotteries, setLotteries] = useState<any[]>([]);
  const [unities, setUnities] = useState<any[]>([]);

  const { user } = useAuth();

  const isSeller = user?.user_type_id === TypeUser.seller;
  const isUnityResponsible = user?.user_type_id === TypeUser.unity_responsible;
  const isOwner = user?.user_type_id === TypeUser.client;

  const hasAccess = isSeller || isUnityResponsible || isOwner;

  const getPageTitle = () => {
    if (isSeller) return "Histórico - Vendedor para Unidade";
    if (isUnityResponsible) return "Histórico - Recebidos de Vendedores";
    return "Histórico - Vendedor para Unidade";
  };

  const getPageDescription = () => {
    if (isSeller) return "Acompanhe suas distribuições para unidade";
    if (isUnityResponsible) return "Acompanhe as distribuições recebidas dos vendedores";
    return "Acompanhe todas as distribuições de vendedores para unidades";
  };

  const loadSelectData = useCallback(async () => {
    try {
      const lotteriesRes = await getProductGroupsData(1);
      if (lotteriesRes.data?.response?.data) {
        setLotteries(lotteriesRes.data.response.data);
      }

      // Carrega unidades apenas para o dono
      if (isOwner) {
        const unitiesRes = await getSelectUnitiesOnlyActives();
        if (unitiesRes.data?.response) {
          setUnities(unitiesRes.data.response);
        }
      }
    } catch (error) {
      console.error("Error loading select data:", error);
    }
  }, [isOwner]);

  const fetchData = useCallback(async (page: number = 1) => {
    if (!hasAccess) return;

    setLoading(true);
    try {
      const params: any = {
        page,
        distribution_type_id: 5, // Vendedor -> Unidade
        contest_status_id: filters.contest_status_id,
        contest_id: selectedContest?.id,
        lottery_id: selectedLottery?.id,
        start_date: filters.start_date ? format(filters.start_date, "yyyy-MM-dd") : undefined,
        end_date: filters.end_date ? format(filters.end_date, "yyyy-MM-dd") : undefined,
        status_id: filters.status_id,
      };

      // Filtros específicos por tipo de usuário
      if (isSeller) {
        // Vendedor vê apenas suas próprias distribuições
        params.from_seller_id = selectedSenderSeller?.id || user?.seller?.id;
      } else if (isUnityResponsible) {
        // Responsável pela unidade vê distribuições recebidas na unidade dele
        params.unity_id = user?.unity_id;
        // Pode filtrar por vendedor remetente
        if (selectedSenderSeller?.id) {
          params.from_seller_id = selectedSenderSeller.id;
        }
      } else if (isOwner) {
        // Dono vê tudo, mas pode filtrar
        if (selectedSenderSeller?.id) {
          params.from_seller_id = selectedSenderSeller.id;
        }
        // Filtro de unidade de destino (apenas para dono)
        if (selectedUnity?.id) {
          params.unity_id = selectedUnity.id;
        }
      }

      // Filtro adicional de vendedor destinatário (se selecionado)
      if (selectedSeller?.id) {
        params.seller_id = selectedSeller.id;
      }

      const { data } = await getDistributionProcessesAll(params);

      if (data?.response) {
        setData(data.response);
      }
    } catch (error) {
      toast.error("Erro ao carregar histórico de distribuições");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedContest?.id, selectedLottery?.id, selectedSeller?.id, selectedSenderSeller?.id, selectedUnity?.id, user, hasAccess, isSeller, isUnityResponsible, isOwner]);

  useEffect(() => {
    if (hasAccess) {
      loadSelectData();
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    if (data !== null && hasAccess) {
      fetchData(1);
    }
  }, [filters, selectedContest, selectedLottery, selectedSeller, selectedSenderSeller, selectedUnity]);

  const searchSellers = async (query: string) => {
    const response = await getSellersDataWithFilters({ name: query, is_active: true, per_page: 20 });
    return response.data?.response?.data || [];
  };

  const handleFilterChange = (key: keyof FilterData, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
    }));
  };

  const handleApplyFilters = () => {
    fetchData(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSelectedContest(null);
    setSelectedSeller(null);
    setSelectedLottery(null);
    setSelectedSenderSeller(null);
    setSelectedUnity(null);
    fetchData(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (data?.last_page || 1)) {
      fetchData(page);
    }
  };

  const pageActions = {
    previous: () => handlePageChange((data?.current_page || 1) - 1),
    next: () => handlePageChange((data?.current_page || 1) + 1),
    lastPage: () => handlePageChange(data?.last_page || 1),
  };

  if (!hasAccess) {
    return (
      <PageTemplate>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Você não tem permissão para acessar esta página.</p>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {getPageTitle()}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {getPageDescription()}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchData(data?.current_page || 1)}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
              {(Object.keys(filters).filter(k => filters[k as keyof FilterData] !== undefined).length > 0 ||
                selectedContest || selectedSeller || selectedLottery || selectedSenderSeller || selectedUnity) && (
                <Badge className="ml-2 bg-red-500 text-white">
                  {Object.keys(filters).filter(k => filters[k as keyof FilterData] !== undefined).length +
                   [selectedContest, selectedSeller, selectedLottery, selectedSenderSeller, selectedUnity]
                   .filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {(selectedContest || selectedSeller || selectedLottery || selectedSenderSeller || selectedUnity ||
          Object.keys(filters).some(k => filters[k as keyof FilterData] !== undefined)) && (
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros ativos:</span>
                {selectedContest && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Concurso: {selectedContest.contest_number || selectedContest.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedContest(null)} />
                  </Badge>
                )}
                {selectedLottery && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selectedLottery.color }} />
                    {selectedLottery.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedLottery(null)} />
                  </Badge>
                )}
                {selectedSeller && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Vendedor: {selectedSeller.user?.name || selectedSeller.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedSeller(null)} />
                  </Badge>
                )}
                {selectedSenderSeller && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    De: {selectedSenderSeller.user?.name || selectedSenderSeller.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedSenderSeller(null)} />
                  </Badge>
                )}
                {selectedUnity && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Unidade: {selectedUnity.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedUnity(null)} />
                  </Badge>
                )}
                {filters.status_id && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Status: {processStatusOptions.find(o => o.value === filters.status_id?.toString())?.label}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange("status_id", undefined)} />
                  </Badge>
                )}
                {filters.start_date && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    De: {format(filters.start_date, "dd/MM/yyyy")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange("start_date", undefined)} />
                  </Badge>
                )}
                {filters.end_date && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Até: {format(filters.end_date, "dd/MM/yyyy")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange("end_date", undefined)} />
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {showFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Status do Concurso</Label>
                  <Select
                    value={filters.contest_status_id?.toString() || "all"}
                    onValueChange={(value) => handleFilterChange("contest_status_id", value === "all" ? undefined : Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {contestStatusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status do Processo</Label>
                  <Select
                    value={filters.status_id?.toString() || "all"}
                    onValueChange={(value) => handleFilterChange("status_id", value === "all" ? undefined : Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {processStatusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Loteria</Label>
                  <Select
                    value={selectedLottery?.id?.toString() || "all"}
                    onValueChange={(value) => {
                      if (value === "all") {
                        setSelectedLottery(null);
                      } else {
                        const lottery = lotteries.find(l => l.id.toString() === value);
                        setSelectedLottery(lottery);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {lotteries.map(lottery => (
                        <SelectItem key={lottery.id} value={lottery.id.toString()}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: lottery.color }}
                            />
                            {lottery.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Concurso</Label>
                  <SearchableSelect
                    placeholder="Buscar concurso"
                    searchFunction={(query) => searchContestsData({ name: query })}
                    onSelect={setSelectedContest}
                    value={selectedContest}
                    formatOption={(option) => `${option.contest_number || option.name} - ${option.lottery?.name || ''}`}
                  />
                </div>

                {/* Filtro de vendedor remetente - apenas para não-vendedores */}
                {!isSeller && (
                  <div className="space-y-2">
                    <Label>Vendedor Remetente</Label>
                    <SearchableSelect
                      placeholder="Buscar vendedor"
                      searchFunction={searchSellers}
                      onSelect={setSelectedSenderSeller}
                      value={selectedSenderSeller}
                      formatOption={(option) => option.user?.name || option.name || `Vendedor #${option.id}`}
                    />
                  </div>
                )}

                {/* Filtro de unidade de destino - apenas para dono */}
                {isOwner && (
                  <div className="space-y-2">
                    <Label>Unidade de Destino</Label>
                    <Select
                      value={selectedUnity?.id?.toString() || "all"}
                      onValueChange={(value) => {
                        if (value === "all") {
                          setSelectedUnity(null);
                        } else {
                          const unity = unities.find(u => u.id.toString() === value);
                          setSelectedUnity(unity);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {unities.map(unity => (
                          <SelectItem key={unity.id} value={unity.id.toString()}>
                            {unity.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Data Inicial</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.start_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.start_date ? format(filters.start_date, "dd/MM/yyyy") : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.start_date}
                        onSelect={(date) => handleFilterChange("start_date", date)}
                        locale={ptBR}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Data Final</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.end_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.end_date ? format(filters.end_date, "dd/MM/yyyy") : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.end_date}
                        onSelect={(date) => handleFilterChange("end_date", date)}
                        locale={ptBR}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Limpar
                </Button>
                <Button onClick={handleApplyFilters}>
                  <Filter className="h-4 w-4 mr-2" />
                  Aplicar Filtros
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Carregando...</p>
            </CardContent>
          </Card>
        ) : (
          data && <TableData dataApi={data} pageActions={pageActions} />
        )}
      </div>
    </PageTemplate>
  );
}
